import React, { useEffect, useState, useMemo, useCallback, memo } from 'react';
import EcoSpace from 'eco-design/base/eco-space';
import { useLang } from '@/i18n';
import styles from './index.module.scss';
import editorEventsBus from 'lowcode/utils/editorEventsBus';
import EcoTitle from 'eco-design/base/eco-title';
import { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/scss';
import EcoIconSwiperArrow from 'eco-design-micro/icons/eco-icon-swiper-arrow';
import EcoImg from 'eco-design/base/eco-img';
import EcoButton from 'eco-design/base/eco-button';
import { getSolarRecommand } from '@/service/solar-calculator';
import { useRouter } from 'next/router';
import gtm from '@/utils/gtm';

const SectionI: React.FC<any> = ({}) => {
    const { t } = useLang('theProduct.SectionI');
    const [currentProducts, setCurrentProducts] = useState<any[]>([]);
    const { locale = 'jp' } = useRouter();
    const sectionRef = React.useRef<HTMLDivElement>(null);
    const [hasLoaded, setHasLoaded] = useState(false);

    const itemsA = useMemo(
        () => [
            {
                title: t('item2_title'),
                desc: t('item2_desc'),
                lChildren: t('btn_learn_more'),
                lTheme: 'blue',
                lType: 'filled',
                pc: 'https://websiteoss.ecoflow.com/upload/image/1761646126055/sectioni_2.png',
                productId: (() => {
                    const map: any = {
                        jp: '14794729652591',
                    };
                    return map[locale] || '';
                })(),
                variantId: (() => {
                    const map: any = {
                        jp: '52242011357551',
                    };
                    return map[locale] || '';
                })(),
            },
            {
                title: t('item4_title'),
                desc: t('item4_desc'),
                lChildren: t('btn_learn_more'),
                lTheme: 'blue',
                lType: 'filled',
                pc: 'https://websiteoss.ecoflow.com/upload/image/1761646125866/sectioni_4.png',
                productId: (() => {
                    const map: any = {
                        jp: '14794729554287',
                    };
                    return map[locale] || '';
                })(),
                variantId: (() => {
                    const map: any = {
                        jp: '52242011226479',
                    };
                    return map[locale] || '';
                })(),
            },
            {
                title: t('item5_title'),
                desc: t('item5_desc'),
                lChildren: t('btn_learn_more'),
                lTheme: 'blue',
                lType: 'filled',
                pc: 'https://websiteoss.ecoflow.com/upload/image/1761646126500/sectioni_5.png',
                productId: (() => {
                    const map: any = {
                        jp: '14794729521519',
                    };
                    return map[locale] || '';
                })(),
                variantId: (() => {
                    const map: any = {
                        jp: '52242011193711',
                    };
                    return map[locale] || '';
                })(),
            },
        ],
        [t, locale],
    );

    const handleAddToCart = useCallback(
        (item: any, index: number) => {
            gtm.push({
                event: 'uaEvent',
                event_name: 'checkout',
                button_name: t('btn_learn_more'),
                __btn_id1: `RV-sectionI .card-${index + 1}`,
                __btn_id2: item?.variantId,
                __btn_type: 'common',
            });

            const ProductsArr = [
                {
                    item: {
                        variantId: item?.variantId,
                        productId: item?.productId,
                        // 产品信息
                        name: item?.productTitle || item?.skuTitle || '',
                        title: item?.productTitle || item?.skuTitle || '',
                        img: item?.variantImg || item?.img,
                        // 价格信息
                        price: item?.price,
                        discountPrice: item?.price,
                        originalPrice: item?.compareAtPrice,
                        compareAtPrice: item?.compareAtPrice,
                        // 库存和限制
                        inventoryQuantity: item?.inventoryQuantity,
                        maxCanBuyCount: item?.maxCanBuyCount || item?.inventoryQuantity || 999,
                    },
                    quantity: 1,
                },
            ];
            try {
                editorEventsBus.emit('efshopingcart-add', ProductsArr);
            } catch (error) {
                console.error('添加到购物车时发生错误:', error);
            }
        },
        [t],
    );

    const getAsynItem = useCallback(
        async (item: any) => {
            const { productId, variantId } = item;
            if (productId && variantId) {
                return await new Promise(async (resolve, reject) => {
                    try {
                        const res = await getSolarRecommand({ productId, variantId }, locale);
                        if (res?.code === '0') {
                            item.discountPrice = res?.data?.price;
                            item.price = res?.data?.price;
                            item.compareAtPrice = res?.data?.compareAtPrice || res?.data?.price;
                            item.inventoryQuantity = res?.data?.inventoryQuantity ?? undefined;
                            item.skuTitle = res?.data?.skuTitle || '';
                            item.productTitle = res?.data?.productTitle || '';
                            item.variantImg = res?.data?.variantImg || '';

                            return resolve(res);
                        } else {
                            console.error(res?.message);
                            return reject(res?.message);
                        }
                    } catch (e) {
                        console.error(e);
                        return reject(e);
                    }
                });
            }
            return;
        },
        [locale],
    );

    // 使用 Intersection Observer 监听组件是否接近视口
    useEffect(() => {
        const currentRef = sectionRef.current;
        if (!currentRef || hasLoaded) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    // 当组件距离视口顶部 200px 时触发
                    if (entry.isIntersecting && !hasLoaded) {
                        setHasLoaded(true);

                        try {
                            const groupProducts = JSON.parse(JSON.stringify(itemsA));
                            const promises = groupProducts?.map((item: any) => getAsynItem(item));
                            Promise.all(promises)
                                .then(() => {
                                    const result = groupProducts;
                                    setCurrentProducts(result);
                                })
                                .catch((e) => {
                                    console.error(e);
                                });
                        } catch (error) {
                            console.error('获取产品信息时发生错误:', error);
                        }
                    }
                });
            },
            {
                // rootMargin: 距离视口顶部 200px 时触发
                rootMargin: '200px 0px 0px 0px',
                threshold: 0,
            },
        );

        observer.observe(currentRef);

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [itemsA, getAsynItem, hasLoaded]);

    return (
        <div ref={sectionRef} className={`${styles.SectionI} GtmModuleData-section RV-sectionI`} data-section-id="on-the-road-SectionI">
            <EcoSpace />
            <EcoTitle title={t('title')} className={`eco-content`} />
            <Swiper
                className={`section-i-items`}
                slidesPerView={'auto'}
                spaceBetween={16}
                navigation={{
                    nextEl: '.eco-swiper-button-next',
                    prevEl: '.eco-swiper-button-prev',
                }}
                modules={[Navigation]}
            >
                {currentProducts?.map((item: any, index: number) => {
                    const { title, desc } = item;
                    // const soldOutBtn = Number(item?.inventoryQuantity || 0) <= 0 ? TextA.soldOut : item?.lChildren || undefined;
                    return (
                        <SwiperSlide key={index} className={`section-i-item`}>
                            <div className="section-i-item-img-box">
                                <EcoImg pc={item?.pc || item?.variantImg} className={`section-i-items-item-img`} isOpenLazy />
                            </div>
                            <div className={`section-i-item-msg`}>
                                <div className={`section-i-item-title`}>{title}</div>
                                <div className={`section-i-item-desc`}>{desc}</div>
                                <div className={`section-i-item-btn-box`}>
                                    <EcoButton
                                        data-btn-id={item?.variantId}
                                        className={`GtmModuleData-btn-common card-${index + 1}`}
                                        lRadius="ellipse"
                                        lSize="large"
                                        {...item}
                                        lChildren={t('btn_learn_more')}
                                        onClick={item?.inventoryQuantity <= 0 ? undefined : () => handleAddToCart(item, index)}
                                        lDisabled={item?.inventoryQuantity <= 0}
                                    />
                                </div>
                            </div>
                        </SwiperSlide>
                    );
                })}
                <div className={`eco-swiper-control`}>
                    <div className="eco-swiper-navigation">
                        <div className="eco-swiper-button-prev">
                            <EcoIconSwiperArrow className="eco-swiper-button-icon" />
                        </div>
                        <div className="eco-swiper-button-next">
                            <EcoIconSwiperArrow className="eco-swiper-button-icon" />
                        </div>
                    </div>
                </div>
            </Swiper>
            <EcoSpace />
        </div>
    );
};

export default memo(SectionI);
